<template>
  <div
    class="bg-blue-50 rounded-lg border border-blue-100"
    :class="compact ? 'p-4' : 'p-6'"
    data-testid="upsell-extension"
  >
    <!-- Inklapbaar (stap 2): alleen de kop, met de gekozen verlenging als samenvatting -->
    <button
      v-if="collapsible"
      type="button"
      class="w-full flex items-center justify-between text-left"
      :aria-expanded="open"
      data-testid="upsell-toggle"
      @click="open = !open"
    >
      <span class="font-semibold text-blue-900 flex items-center">
        <span class="mr-2">⏰</span> Meer tijd nodig?
        <span v-if="selectedHours > 0" class="ml-2 text-sm font-normal text-blue-800" data-testid="upsell-summary">
          (+{{ selectedHours }} uur gekozen)
        </span>
        <span v-else class="ml-2 text-sm font-normal text-blue-700">
          Verleng met 1 of 2 uur voor {{ upsell.hourly_rate_formatted }} per uur
        </span>
      </span>
      <svg class="w-5 h-5 text-blue-700 shrink-0 transition-transform" :class="open ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    <h3 v-else class="font-semibold text-blue-900 mb-1 flex items-center">
      <span class="mr-2">⏰</span> Meer tijd nodig?
    </h3>

    <div v-show="!collapsible || open" :class="collapsible ? 'mt-4' : ''" data-testid="upsell-body">
    <p class="text-sm text-blue-800 mb-4">
      Verleng je sessie nu al met 1 of 2 uur voor
      <strong>{{ upsell.hourly_rate_formatted }} per uur</strong>, hetzelfde gemiddelde uurtarief als je gekozen pakket.
      {{ hint }}
    </p>

    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <button
        type="button"
        @click="select(0)"
        :disabled="processing"
        :aria-pressed="selectedHours === 0"
        data-testid="upsell-option-0"
        class="text-left rounded-lg border-2 p-4 transition-colors disabled:cursor-not-allowed"
        :class="selectedHours === 0
          ? 'border-blue-600 bg-white shadow-sm'
          : 'border-blue-200 bg-white/60 hover:border-blue-400'"
      >
        <div class="font-semibold text-gray-900">Geen verlenging</div>
        <div class="text-sm text-gray-600 mt-1">{{ upsell.base_hours }} uur, tot {{ upsell.base_end_time }}</div>
        <div class="text-sm font-medium text-gray-700 mt-2">+ €0,00</div>
      </button>

      <button
        v-for="option in upsell.options"
        :key="option.hours"
        type="button"
        @click="select(option.hours)"
        :disabled="processing || !option.available"
        :aria-pressed="selectedHours === option.hours"
        :data-testid="`upsell-option-${option.hours}`"
        class="text-left rounded-lg border-2 p-4 transition-colors disabled:cursor-not-allowed"
        :class="selectedHours === option.hours
          ? 'border-blue-600 bg-white shadow-sm'
          : option.available
            ? 'border-blue-200 bg-white/60 hover:border-blue-400'
            : 'border-gray-200 bg-gray-50 opacity-60'"
      >
        <div class="font-semibold text-gray-900">+{{ option.hours }} uur</div>
        <div class="text-sm text-gray-600 mt-1">
          <template v-if="option.available">{{ option.new_duration_hours }} uur, tot {{ option.new_end_time }}</template>
          <template v-else>{{ option.reason || 'Niet beschikbaar' }}</template>
        </div>
        <div class="text-sm font-medium mt-2" :class="option.available ? 'text-blue-800' : 'text-gray-500'">
          + {{ option.price_formatted }}
        </div>
      </button>
    </div>

    <p v-if="processing" class="mt-3 text-sm text-blue-700">Verlenging bijwerken...</p>
    <p v-if="notice" class="mt-3 text-sm text-amber-800 bg-amber-50 p-3 rounded-lg border border-amber-200" data-testid="upsell-notice">
      {{ notice }}
    </p>
    <p v-if="error" class="mt-3 text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200" data-testid="upsell-error">
      {{ error }}
    </p>
    </div>
  </div>
</template>

<script setup lang="ts">
// Verleng-upsell: keuze tussen geen, +1 of +2 uur, tegen het gemiddelde
// uurtarief van het gekozen pakket. Gebruikt in het boekingsformulier (stap 2
// en 3, nog vóór de boeking bestaat) en op de betaalpagina (op de openstaande
// boeking). De ouder bepaalt wat een keuze doet; dit component toont alleen.
import { ref } from 'vue'
import type { UpsellExtension } from '@/types'

const props = withDefaults(defineProps<{
  upsell: UpsellExtension
  selectedHours: number
  processing?: boolean
  notice?: string | null
  error?: string | null
  hint?: string
  compact?: boolean
  /** Ingeklapt tonen (alleen de kop), uit te vouwen door de bezoeker. */
  collapsible?: boolean
}>(), {
  processing: false,
  notice: null,
  error: null,
  hint: 'Je kunt je keuze aanpassen tot je betaalt.',
  compact: false,
  collapsible: false,
})

// Ingeklapt starten; staat er al een verlenging gekozen, dan open.
const open = ref(!props.collapsible || props.selectedHours > 0)

const emit = defineEmits<{ select: [hours: number] }>()

const select = (hours: number) => emit('select', hours)
</script>
